import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Grid, List, SortAsc, SortDesc, Filter, X, Folder, BookOpen, Wand2, Edit, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { HierarchyTree } from './HierarchyTree';
import { FolderCard } from './FolderCard';
import { BreadcrumbNav } from './BreadcrumbNav';
import TutorialCard from './TutorialCard';
import { apiService } from '../services/api';
import { HierarchyNode } from '../types';
import { cn } from '../utils/cn';
import { useSearchParams } from 'react-router-dom';

interface SelectionResult {
  node: HierarchyNode;
  path: HierarchyNode[];
}

const findNodeWithPath = (
  nodes: HierarchyNode[],
  target: string,
  path: HierarchyNode[] = []
): SelectionResult | null => {
  for (const node of nodes) {
    const nextPath = [...path, node];
    if (node.type === 'category' && (node.category?.name || node.name || '').toLowerCase() === target.toLowerCase()) {
      return { node, path: nextPath };
    }

    if (node.children && node.children.length > 0) {
      const found = findNodeWithPath(node.children, target, nextPath);
      if (found) {
        return found;
      }
    }
  }

  return null;
};

type ViewMode = 'grid' | 'list';
type SortOption = 'name' | 'count' | 'recent';

interface HierarchyBrowserProps {
  initialCategoryName?: string;
}

export const HierarchyBrowser: React.FC<HierarchyBrowserProps> = ({ initialCategoryName }) => {
  const navigate = useNavigate();
  const [hierarchy, setHierarchy] = useState<HierarchyNode[]>([]);
  const [currentPath, setCurrentPath] = useState<HierarchyNode[]>([]);
  const [currentNode, setCurrentNode] = useState<HierarchyNode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [showEmpty, setShowEmpty] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    loadHierarchy();
  }, []);

  useEffect(() => {
    if (!hierarchy.length) return;
    const categoryParam = initialCategoryName || searchParams.get('category') || '';
    if (!categoryParam) return;

    const result = findNodeWithPath(hierarchy, categoryParam);
    if (result) {
      setCurrentNode(result.node);
      setCurrentPath(result.path);
    }
  }, [hierarchy, initialCategoryName, searchParams]);

  const loadHierarchy = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getHierarchyTree();
      if (response.success && response.data) {
        setHierarchy(response.data);
      } else {
        setError('Failed to load hierarchy');
      }
    } catch (err: any) {
      console.error('Failed to load hierarchy:', err);
      setError(err.message || 'Failed to load hierarchy');
    } finally {
      setLoading(false);
    }
  };

  const handleNodeClick = (node: HierarchyNode) => {
    if (node.type === 'tutorial') {
      navigate(`/tutorials/${node.tutorial!.id}`);
      return;
    }

    const categoryName = encodeURIComponent(node.category?.name || node.name || '');
    if (categoryName) {
      navigate(`/categories/${categoryName}`);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      // Home clicked - go to root
      setCurrentPath([]);
      setCurrentNode(null);
      return;
    }

    const newPath = currentPath.slice(0, index + 1);
    setCurrentPath(newPath);
    setCurrentNode(newPath[newPath.length - 1]);
  };

  const getTutorialProgress = (_tutorialId: string): number => {
    // Mock progress - in real app, this would come from user state
    return 0;
  };

  // Determine what to display - must be before early returns
  const displayNode = currentNode || null;
  const rawChildren = displayNode?.children || hierarchy;

  // Filter and sort children - must be before early returns
  const filteredAndSortedChildren = useMemo(() => {
    let filtered = rawChildren || [];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(child => {
        const name = child.name?.toLowerCase() || '';
        const description = (child.category?.description || '').toLowerCase();
        return name.includes(query) || description.includes(query);
      });
    }

    // Filter out empty categories if showEmpty is false
    if (!showEmpty && displayNode?.type === 'category') {
      filtered = filtered.filter(child => {
        if (child.type === 'category') {
          return (child.tutorialCount || 0) > 0;
        }
        return true;
      });
    }

    // Sort: First by type (categories before tutorials), then by selected sort option
    const sorted = [...filtered].sort((a, b) => {
      // First, sort by type: categories come before tutorials
      const typeOrder = { category: 0, tutorial: 1 };
      const typeComparison = (typeOrder[a.type as keyof typeof typeOrder] || 1) - 
                            (typeOrder[b.type as keyof typeof typeOrder] || 1);
      
      if (typeComparison !== 0) {
        return typeComparison;
      }
      
      // If same type, sort by selected option
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'count':
          comparison = (a.tutorialCount || 0) - (b.tutorialCount || 0);
          break;
        case 'recent':
          // For recent, we'd need createdAt on nodes - for now, use name
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
      }
      
      return sortAsc ? comparison : -comparison;
    });

    return sorted;
  }, [rawChildren, searchQuery, sortBy, sortAsc, showEmpty, displayNode?.type]);

  const children = filteredAndSortedChildren;

  // Early returns after all hooks
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-xl mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600">Loading hierarchy...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={loadHierarchy}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row h-full bg-gray-50">
      {/* Sidebar - Hierarchy Tree */}
      <div className="w-full lg:w-72 border-r bg-white shadow-sm overflow-y-auto max-h-[600px] lg:max-h-none lg:sticky lg:top-0 lg:h-screen">
        <div className="p-5 border-b bg-gradient-to-r from-primary-50 to-primary-100">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider flex items-center gap-2">
            <Folder className="w-4 h-4 text-primary-600" />
            Navigation
          </h2>
          <p className="text-xs text-gray-600 mt-1">Browse categories</p>
        </div>
        <div className="p-2">
          <HierarchyTree
            nodes={hierarchy}
            onNodeClick={handleNodeClick}
            selectedNodeId={currentNode?.id}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 lg:p-8 overflow-y-auto">
        <BreadcrumbNav
          path={currentPath}
          onNavigate={handleBreadcrumbClick}
        />

        {!displayNode ? (
          // Root view - show top-level categories
          <div>
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">All Categories</h1>
              <p className="text-gray-600 text-lg">{hierarchy.length} categor{hierarchy.length !== 1 ? 'ies' : 'y'} available</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {hierarchy.map(node => (
                node.type === 'category' && (
                  <FolderCard
                    key={node.id}
                    category={node.category!}
                    onClick={() => handleNodeClick(node)}
                    tutorialCount={node.tutorialCount || 0}
                  />
                )
              ))}
            </div>
          </div>
        ) : displayNode.type === 'category' ? (
          // Category view - show subcategories and tutorials
          <div>
            {/* Header Section */}
            <div className="mb-8">
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    {displayNode.category?.icon && (
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl shadow-md"
                        style={{
                          backgroundColor: displayNode.category.color ? `${displayNode.category.color}20` : '#667eea20',
                          color: displayNode.category.color || '#667eea'
                        }}
                      >
                        {displayNode.category.icon}
                      </div>
                    )}
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900">{displayNode.name}</h1>
                      {displayNode.category?.description && (
                        <p className="text-gray-600 mt-2 text-lg">{displayNode.category.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-gray-500 mt-4">
                    <span className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      {displayNode.tutorialCount || 0} {displayNode.tutorialCount === 1 ? 'tutorial' : 'tutorials'}
                    </span>
                    {children.filter(c => c.type === 'category').length > 0 && (
                      <span className="flex items-center gap-1">
                        <Folder className="w-4 h-4" />
                        {children.filter(c => c.type === 'category').length} {children.filter(c => c.type === 'category').length === 1 ? 'subcategory' : 'subcategories'}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Search and Filters - Enhanced */}
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 mb-6">
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search - Enhanced */}
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search categories and tutorials..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-base shadow-sm hover:border-gray-300"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
                        title="Clear search"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {/* View Mode Toggle - Enhanced */}
                  <div className="flex items-center gap-2 bg-gray-50 p-1 rounded-lg">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={cn(
                        "p-2.5 rounded-lg transition-all duration-200",
                        viewMode === 'grid'
                          ? "bg-white text-primary-600 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      )}
                      title="Grid view"
                    >
                      <Grid className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={cn(
                        "p-2.5 rounded-lg transition-all duration-200",
                        viewMode === 'list'
                          ? "bg-white text-primary-600 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      )}
                      title="List view"
                    >
                      <List className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Sort - Enhanced */}
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as SortOption)}
                        className="appearance-none px-4 py-2.5 pr-10 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-sm font-medium bg-white shadow-sm hover:border-gray-300 cursor-pointer"
                      >
                        <option value="name">Sort by Name</option>
                        <option value="count">Sort by Count</option>
                        <option value="recent">Sort by Recent</option>
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <Filter className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                    <button
                      onClick={() => setSortAsc(!sortAsc)}
                      className={cn(
                        "p-2.5 rounded-xl border-2 border-gray-200 hover:border-gray-300 transition-all shadow-sm hover:shadow",
                        sortAsc ? "bg-white" : "bg-gray-50"
                      )}
                      title={sortAsc ? 'Ascending' : 'Descending'}
                    >
                      {sortAsc ? <SortAsc className="w-5 h-5 text-gray-700" /> : <SortDesc className="w-5 h-5 text-gray-700" />}
                    </button>
                  </div>

                  {/* Show Empty Toggle - Enhanced */}
                  <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors">
                      <input
                        type="checkbox"
                        checked={showEmpty}
                        onChange={(e) => setShowEmpty(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 focus:ring-2"
                      />
                      <span>Show empty</span>
                    </label>
                  </div>
                </div>

                {/* Results count - Enhanced */}
                {searchQuery && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        Found <span className="text-primary-600 font-bold">{children.length}</span> {children.length === 1 ? 'item' : 'items'}
                      </span>
                      <button
                        onClick={() => setSearchQuery('')}
                        className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
                      >
                        <X className="w-4 h-4" />
                        Clear
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Content Grid/List */}
            {viewMode === 'grid' ? (
              <div>
                {/* Subcategories Section - Enhanced */}
                {children.filter(child => child.type === 'category').length > 0 && (
                  <div className="mb-10">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Folder className="w-6 h-6 text-primary-600" />
                        Subcategories
                      </h2>
                      <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                        {children.filter(child => child.type === 'category').length} {children.filter(child => child.type === 'category').length === 1 ? 'category' : 'categories'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {children
                        .filter(child => child.type === 'category')
                        .map(child => (
                          <FolderCard
                            key={child.id}
                            category={child.category!}
                            onClick={() => handleNodeClick(child)}
                            tutorialCount={child.tutorialCount || 0}
                          />
                        ))}
                    </div>
                  </div>
                )}

                {/* Tutorials Section - Enhanced */}
                {children.filter(child => child.type === 'tutorial').length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <BookOpen className="w-6 h-6 text-primary-600" />
                        {children.filter(child => child.type === 'category').length > 0 ? 'Tutorials' : 'All Content'}
                      </h2>
                      <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                        {children.filter(child => child.type === 'tutorial').length} {children.filter(child => child.type === 'tutorial').length === 1 ? 'tutorial' : 'tutorials'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {children
                        .filter(child => child.type === 'tutorial')
                        .map(child => (
                          <TutorialCard
                            key={child.id}
                            tutorial={child.tutorial!}
                            progress={getTutorialProgress(child.tutorial!.id)}
                            onStart={() => navigate(`/tutorials/${child.tutorial!.id}`)}
                            onContinue={() => navigate(`/tutorials/${child.tutorial!.id}`)}
                          />
                        ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {/* Subcategories Section - Enhanced List View */}
                {children.filter(child => child.type === 'category').length > 0 && (
                  <div className="mb-8">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Folder className="w-6 h-6 text-primary-600" />
                        Subcategories
                      </h2>
                      <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                        {children.filter(child => child.type === 'category').length} {children.filter(child => child.type === 'category').length === 1 ? 'category' : 'categories'}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {children
                        .filter(child => child.type === 'category')
                        .map(child => (
                    <motion.div
                      key={child.id}
                      onClick={() => handleNodeClick(child)}
                      whileHover={{ x: 4 }}
                      className="bg-white rounded-xl border-2 border-gray-100 p-5 hover:border-primary-200 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 flex-1 min-w-0">
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md group-hover:shadow-lg transition-all"
                            style={{ 
                              backgroundColor: child.category?.color ? `${child.category.color}15` : '#667eea15',
                              color: child.category?.color || '#667eea',
                              border: `2px solid ${child.category?.color ? `${child.category.color}30` : '#667eea30'}`
                            }}
                          >
                            {child.category?.icon ? (
                              <span className="text-2xl">{child.category.icon}</span>
                            ) : (
                              <Folder className="w-6 h-6" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-gray-900 truncate text-lg group-hover:text-primary-600 transition-colors">{child.name}</h3>
                            {child.category?.description && (
                              <p className="text-sm text-gray-600 line-clamp-1 mt-1">{child.category.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-sm font-semibold text-gray-700 bg-gray-50 px-3 py-1.5 rounded-lg ml-4 group-hover:bg-primary-50 group-hover:text-primary-700 transition-colors">
                          {child.tutorialCount || 0} {child.tutorialCount === 1 ? 'tutorial' : 'tutorials'}
                        </div>
                      </div>
                    </motion.div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Tutorials Section - Enhanced List View */}
                {children.filter(child => child.type === 'tutorial').length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <BookOpen className="w-6 h-6 text-primary-600" />
                        {children.filter(child => child.type === 'category').length > 0 ? 'Tutorials' : 'All Content'}
                      </h2>
                      <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                        {children.filter(child => child.type === 'tutorial').length} {children.filter(child => child.type === 'tutorial').length === 1 ? 'tutorial' : 'tutorials'}
                      </span>
                    </div>
                    <div className="space-y-4">
                      {children
                        .filter(child => child.type === 'tutorial')
                        .map(child => (
                          <motion.div 
                            key={child.id} 
                            className="bg-white rounded-xl border-2 border-gray-100 hover:border-primary-200 hover:shadow-lg transition-all duration-200 overflow-hidden"
                            whileHover={{ x: 4 }}
                          >
                            <TutorialCard
                              tutorial={child.tutorial!}
                              progress={getTutorialProgress(child.tutorial!.id)}
                              onStart={() => navigate(`/tutorials/${child.tutorial!.id}`)}
                              onContinue={() => navigate(`/tutorials/${child.tutorial!.id}`)}
                            />
                          </motion.div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          // Category view - show tutorials
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">{displayNode.name}</h1>
            {displayNode.category?.description && (
              <p className="text-gray-600 mb-6">{displayNode.category.description}</p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {children
                .filter(child => child.type === 'tutorial')
                .map(child => (
                  <TutorialCard
                    key={child.id}
                    tutorial={child.tutorial!}
                    progress={getTutorialProgress(child.tutorial!.id)}
                    onStart={() => navigate(`/tutorials/${child.tutorial!.id}`)}
                    onContinue={() => navigate(`/tutorials/${child.tutorial!.id}`)}
                  />
                ))}
            </div>
          </div>
        )}

        {children.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
            <div className="max-w-md mx-auto">
              {searchQuery ? (
                <>
                  <Search className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No results found</h3>
                  <p className="text-gray-600 mb-6">
                    No items match your search for "<span className="font-medium">{searchQuery}</span>"
                  </p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                  >
                    Clear search
                  </button>
                </>
              ) : (
                <>
                  <Folder className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No content here</h3>
                  <p className="text-gray-600">
                    This category doesn't have any subcategories or tutorials yet.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

